import get from 'lodash/get';
import warehouse from 'services/warehouse';

import { Product } from 'redux/models';

import Filter from './Filter';
import SearchBase from './SearchBase';
import { convertMmTo } from './utils';

/**
 * Product Alternatives search implementation.
 */
export default class ProductAlternatives extends SearchBase {
  static defaultProps = {
    // This is the product ID we want alternatives for.
    productId: null,

    // [optional] Allows for restricting search by partner brands.
    partnerSlug: '',

    // These are the current search results.
    searchResults: {
      enabledFilters: {},
      enabledSort: null,
      filters: [],
      products: [],
      numberOfResults: null,
      numberOf2dResults: null,
      numberOf3dResults: null,
      searchSorts: {},

      // pagination
      nextPage: 1,
      totalPages: 1
    }
  };

  constructor (overrides) {
    super({ ...ProductAlternatives.defaultProps, ...overrides });
  }

  /** Creates a new ProductAlternatives copy with optional overrides. */
  makeCopy (overrides) {
    const copy = new ProductAlternatives({
      ...ProductAlternatives.defaultProps,
      ...this.fields,
      ...overrides
    });

    return copy;
  }

  /**
   * Returns whether or not the search is async.
   */
  isAsync () {
    return true;
  }

  /**
   * Runs search query generating updated search results.
   */
  async runQuery ({ overrideDefaultProductType, selectedSizeUnit, updatedFilterUiEnabled }) {
    const productId = this.fields.productId;
    const searchValue = this.fields.searchValue;
    const firstPage = 1;
    const response = await warehouse.getProductAlternatives({
      overrideDefaultProductType,
      productId,
      searchValue,
      pageNum: firstPage,
      queryParams: this._searchFilters()
    });

    const products = response.results.map(whProduct => {
      return Product.createProductFromWarehouseProductResponse({ whProduct });
    });

    const whFilters = get(response, 'filters.filters', []);
    const filters = whFilters.map(whFilter => {
      const label = whFilter.filtername;
      const values = whFilter.values.map(value => value.name);
      const queryParams = whFilter.values.map(value => value.param);
      return new Filter({
        label,
        values,
        queryParams
      });
    });

    const whAppliedFilters = get(response, 'appliedFilters', []);
    let appliedFilters = {};
    whAppliedFilters.forEach(whAppliedFilter => {
      const label = whAppliedFilter.filtername;

      appliedFilters[label] = whAppliedFilter.value || [];
    });

    let enabledFilters = {};
    let catalogFilter;
    whFilters.forEach(whFilter => {
      const label = whFilter.filtername;
      const enabled = updatedFilterUiEnabled ? appliedFilters[label] : this.fields.enabledFilters[label];

      if (label === 'Show3DAsset') {
        catalogFilter = whFilter;
      }

      if (enabled && Array.isArray(enabled) && enabled.length > 0) {
        enabledFilters[label] = enabled;
      } else if (enabled) {
        enabledFilters[label] = [enabled];
      }
    });

    // Size filters get special treatment on the backend,
    // and are therefore not included in the filters.filters array.
    // We need to manually add them here. As part of this process,
    // we convert incoming dimensions from mm to whatever the selected
    // units are.
    if (updatedFilterUiEnabled) {
      ['Width', 'Height', 'Depth'].forEach((dimension) => {
        if (appliedFilters[dimension]) {
          enabledFilters[dimension] = [convertMmTo({
            value: appliedFilters[dimension],
            unit: selectedSizeUnit,
            round: true
          })];
        } else {
          enabledFilters[dimension] = [];
        }
      });
    }

    let numberOf3dResults;
    let numberOf2dResults;

    if (catalogFilter) {
      catalogFilter.values.forEach((val) => {
        if (val.name === 'true') {
          numberOf3dResults = val.count;
        } else {
          numberOf2dResults = val.count;
        }
      });
    }

    const searchResults = {
      enabledFilters,
      enabledSort: get(response, 'sort', null),
      filters,
      nextPage: response.nextpage,
      partnerSlug: this.fields.partnerSlug,
      products,
      totalPages: response.totalpages,
      numberOfResults: response.count,
      numberOf2dResults,
      numberOf3dResults,
      searchSorts: response.sortingOptions
    };

    return searchResults;
  }

  /**
   * This search supports caching.
   * @returns {boolean} true
   */
  hasCaching () {
    return true;
  }

  /**
   * Returns cached search results if available.
   * @returns {Object} cache
   * @returns {boolean} cache.hit - True if search result is found in cache
   * @returns {searchResults|null} cache.searchResults - The search results if cache.hit is true; otherwise, null.
   */
  checkCache () {
    const searchResults = this.searchResults();

    if (
      this.fields.productId &&
      searchResults.products.length > 0 &&
      this.enabledFilters() === searchResults.enabledFilters &&
      this.enabledSort() === searchResults.enabledSort &&
      this.fields.partnerSlug === searchResults.partnerSlug
    ) {
      return {
        hit: true,
        searchResults
      };
    }

    return SearchBase.cacheMiss;
  }

  clearCache () {
    this.fields.productId = null;
    this.fields.searchResults.products = [];
    this.fields.searchResults.enabledFilters = false;
    this.fields.searchResults.partnerSlug = '';
    this.fields.searchResults.enabledSort = null;
  }

  /**
   * Load next page of results.
   */
  hasPagination () {
    return true;
  }

  hasNextPage () {
    const { nextPage, totalPages } = this.fields.searchResults;

    if (nextPage < 1 || nextPage > totalPages) {
      return false;
    }

    return true;
  }

  async loadNextPage () {
    const { nextPage, products } = this.fields.searchResults;

    // Handle no nextPage.
    // Just return the current results.
    if (!this.hasNextPage()) {
      return this.fields.searchResults;
    }

    // Request the next page of results
    // and process the response.
    const productId = this.fields.productId;
    const searchValue = this.fields.searchValue;
    const response = await warehouse.getProductAlternatives({
      productId,
      searchValue,
      pageNum: nextPage,
      queryParams: this._searchFilters()
    });

    const addlProducts = response.results.map(whProduct => {
      return Product.createProductFromWarehouseProductResponse({ whProduct });
    });

    return {
      ...this.fields.searchResults, // keep same filters
      nextPage: response.nextpage,
      products: [...products, ...addlProducts], // append new results onto existing
      totalPages: response.totalpages
    };
  }

  /**
   * Returns the products matching the last search execution.
   * @returns {Product[]} products - Matching products
   */
  products () {
    return this.fields.searchResults.products;
  }

  /**
   * Returns the dynamic filters available based
   * on the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableFilters (state) {
    return this.fields.searchResults.filters;
  }

  /**
   * Returns the dynamic sorts available based
   * on the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableSorts (state) {
    return this.fields.searchResults.searchSorts;
  }

  /**
   * Returns the number of results from
   * the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Number} number of results
   */
  numberOfResults ({ state }) {
    return this.fields.searchResults.numberOfResults;
  }

  /**
   * Returns the number of 2d results from
   * the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Number} number of results
   */
  numberOf2dResults ({ state }) {
    return this.fields.searchResults.numberOf2dResults;
  }

  /**
   * Returns the number of 3d results from
   * the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Number} number of results
   */
  numberOf3dResults ({ state }) {
    return this.fields.searchResults.numberOf3dResults;
  }

  /**
   * Returns the current searchResults object.
   * @returns {Object} searchResults
   */
  searchResults () {
    return this.fields.searchResults;
  }

  /**
   * Helper function for generating the search filters
   * query param string from the enabled filters.
   *
   * @private
   */
  _searchFilters = () => {
    let searchFilters = '';

    // Add Partner Slug search filter if provided
    const partnerSlug = this.fields.partnerSlug;
    searchFilters += partnerSlug ? `&partner=${partnerSlug}` : '';

    const enabledSort = this.fields.enabledSort;
    searchFilters += enabledSort ? `&sort=${enabledSort}` : '';

    const queryEnabledFilters = Object.keys(this.fields.enabledFilters);
    if (queryEnabledFilters.length === 0) {
      return searchFilters;
    }

    let queryParams = [];
    queryEnabledFilters.forEach(filterLabel => {
      // format filter request query params
      const filterValues = this.fields.enabledFilters[filterLabel];
      const is3dAssetFilter = filterLabel === 'Show3DAsset';

      (filterValues || []).forEach(filterValue => {
        if (typeof filterValue === 'number' || is3dAssetFilter) {
          queryParams.push(`f=${filterLabel}:${filterValue}`);
        } else {
          const encodedFilterValue = filterValue.replace(/\$/g, '').replace(/&/g, '%26');
          queryParams.push(`f=${filterLabel}:${encodedFilterValue}`);
        }
      });
    });
    if (queryParams.length > 0) {
      searchFilters += ('&' + queryParams.join('&'));
    }
    return searchFilters;
  }
}
